import { DataHallShuParams } from '../dataHallShu';
import { Column, Option, Row, hideColumns } from '../schemas';

/**
 * 【暫定対応】店舗レポート 種別タブの MKS 商圏・自店項目を非表示にするための判定モジュール。
 *
 * Issue: https://github.com/DKClaris/claris-general/issues/6010
 *
 * 背景:
 *   バックエンドが MKS 商圏データ関連のカラムについて、本来出すべき値とは異なる
 *   一律値を返してしまっている。BE 改修が完了するまでの間、フロント側で
 *   種別タブの該当カラム・表示項目（`syoken` / `jiten` 接頭辞の商圏・自店項目）を非表示にする。
 *   表示項目パネルのジャンルフィルター（「商圏」「自店」ボタン）も非表示にする。
 *   `settingsOptionsHall.fields.marketArea` は店舗 MKS 商圏実績タブ用であり、当該ジャンルボタンとは別物。
 *   prefix 対象外の通常項目（台数など）は選択可能。
 *
 * 構成:
 *   - 商圏項目の判定軸は「ハードコード定数（完全一致）」と「prefix 一覧」の OR 結合。
 *   - `settingsOptionsHall.fields.marketArea` は店舗 MKS 商圏実績タブ用の定義であり、
 *     種別タブの非表示判定には使わない（`daisu` 等の通常項目が誤って除外されるため）。
 *   - prefix 判定により、お気に入り由来の比較系コード
 *     （例: `syokenSoDaisuComparison`, `syokenSoDaisuComparisonDifference`）も
 *     一括で除外できる。
 *   - お気に入りの保存データ自体は変更しない。表示時の selector フィルターに加え、
 *     お気に入り適用時・表示項目パターン適用時の API リクエスト（`DataHallShuParams.fields` 等）
 *     および列順（`columnsOrder`）からも商圏項目を除外する。
 *
 * 剥がし方（BE 復旧後）:
 *   1. 本ファイル（`src/domain/hall/hiddenMarketAreaFields.ts`）を削除する。
 *   2. このファイルを参照している以下の箇所をリバートする:
 *      - `src/redux/server/settingsOptionsHall.ts` の暫定対応版 selector
 *      - `src/redux/server/dataHallShu.ts` の暫定対応版 selector
 *      - `src/components/organisms/HallReportsShuTable/HallReportsShuTableColsOptionPanel.tsx`
 *      - `src/components/organisms/HallReportsShuTable/useHallReportsShuTable.ts`
 *      - `settingsOptionsHallShuGenresForDisplaySelector`（ジャンルフィルター用）
 *      - `src/sagas/hallReportsSettingSaga.ts` のお気に入り適用時フィルター
 *   3. 単体テスト（`hiddenMarketAreaFields.spec.ts`）も合わせて削除する。
 */

/**
 * フォールバック用の商圏項目フィールドコード一覧（完全一致）。
 *
 * NOTE: 通常の商圏項目コードは `MARKET_AREA_HIDDEN_FIELD_CODE_PREFIXES`
 *       による prefix 判定で網羅できるため、ここは現状空のままとしている。
 *       prefix では捕捉しきれない例外コードがあれば追記する。
 */
export const MARKET_AREA_HIDDEN_FIELD_CODES: readonly string[] = [];

/**
 * 商圏タブ配下（MKS 商圏データ）のフィールドコード prefix 一覧。
 *
 * 種別タブ API（`/data/hall/shu`）が返すカラム code・お気に入り由来の
 * `setting.fields` のいずれにおいても、MKS 商圏データ関連の項目は次の命名規則を持つ。
 *   - `syoken` … 商圏項目（例: `syokenSoDaisu` 商圏総台数, `syokenKadoRitu` 商圏稼動率）
 *   - `jiten` … 自店項目（例: `jitenSijiRituTaiSyoken` 自店支持率,
 *                           `jitenSenyuRituTaiSyoken` 自店占有率）
 * 比較系派生コード（`*Comparison`, `*ComparisonDifference`）も prefix 判定で網羅する。
 *
 * 参考: 機種集計 MKS 項目（`src/mocks/models/settingsOptionsKi.ts`）でも同じ命名規則。
 */
export const MARKET_AREA_HIDDEN_FIELD_CODE_PREFIXES: readonly string[] = [
  'syoken',
  'jiten',
];

/**
 * 種別タブの表示項目ジャンルフィルターから非表示にするジャンル名一覧。
 *
 * `genres.shu.shu` の「商圏」「自店」は ColsOptionPanel のジャンルボタン用。
 * `fields.marketArea`（MKS 商圏実績タブ用の項目定義）とは別であり、ここではジャンル名のみ除外する。
 */
export const MARKET_AREA_HIDDEN_GENRE_NAMES: readonly string[] = [
  '商圏',
  '自店',
];

/**
 * 商圏タブ配下（商圏・自店）項目のコードかどうかを判定する。
 *
 * 以下のいずれかに該当すれば非表示対象とみなす:
 *   - ハードコード定数 `MARKET_AREA_HIDDEN_FIELD_CODES` に完全一致する
 *   - `MARKET_AREA_HIDDEN_FIELD_CODE_PREFIXES` のいずれかで始まる
 */
export const isHiddenMarketAreaFieldCode = (code: string): boolean => {
  if (MARKET_AREA_HIDDEN_FIELD_CODES.includes(code)) {
    return true;
  }
  return MARKET_AREA_HIDDEN_FIELD_CODE_PREFIXES.some((prefix) =>
    code.startsWith(prefix)
  );
};

/**
 * `Option` 系の配列から商圏項目を除外する。
 *
 * 種別タブの表示項目選択 UI が読む selector で利用する想定。
 */
export const filterOutHiddenMarketAreaOptions = <T extends Option>(
  options: readonly T[]
): T[] => {
  return options.filter((option) => !isHiddenMarketAreaFieldCode(option.code));
};

/**
 * テーブルの `columns` / `rows` から商圏項目を除外する。
 *
 * 内部で既存ユーティリティ `hideColumns`（`src/domain/schemas.ts`）を再利用する。
 * 商圏項目が含まれない場合は引数の `columns` / `rows` をそのまま返す。
 */
export const filterOutHiddenMarketAreaColumns = (
  columns: Column[],
  rows: Row[]
): { columns: Column[]; rows: Row[] } => {
  const hiddenColumnCodes = columns
    .map((column) => column.code)
    .filter((code) => isHiddenMarketAreaFieldCode(code));

  return hideColumns(columns, rows, hiddenColumnCodes);
};

/**
 * 文字列配列（お気に入り由来の `setting.fields` 等）から商圏項目コードを除外する。
 *
 * 「現在選択中の表示項目」表示用の selector で利用する想定。
 */
export const filterOutHiddenMarketAreaCodes = (
  codes: readonly string[]
): string[] => {
  return codes.filter((code) => !isHiddenMarketAreaFieldCode(code));
};

/**
 * 表示項目パターンの `fields` 配列（列順の元）から商圏・自店項目コードを除外する。
 *
 * パターン保存データに `syoken*` / `jiten*` が残っていても、適用時には送らない。
 */
export const filterOutHiddenMarketAreaPatternFieldCodes = (
  patternFieldCodes: readonly string[]
): string[] => filterOutHiddenMarketAreaCodes(patternFieldCodes);

/**
 * ジャンル名の配列から種別タブの商圏・自店ジャンルを除外する。
 *
 * 種別タブの ColsOptionPanel ジャンルフィルター（「商圏」「自店」ボタン）で利用する。
 */
export const filterOutHiddenMarketAreaGenres = (
  genres: readonly string[]
): string[] => {
  return genres.filter(
    (genre) => !MARKET_AREA_HIDDEN_GENRE_NAMES.includes(genre)
  );
};

/**
 * 種別実績テーブル API 用の検索条件から商圏項目を除外する。
 *
 * お気に入り適用時に、保存済みの `setting.fields` に商圏項目が含まれていても
 * リクエストには送らない。除外後に `fields` が空になる場合は `undefined` とし、
 * API のデフォルト表示項目に委ねる。`sort` が商圏項目を指す場合も除外する。
 */
export const omitHiddenMarketAreaFromDataHallShuParams = (
  params: DataHallShuParams
): DataHallShuParams => {
  const filteredFields =
    params.fields == null
      ? undefined
      : filterOutHiddenMarketAreaCodes(params.fields);

  const fields =
    filteredFields != null && filteredFields.length === 0
      ? undefined
      : filteredFields;

  const sort =
    params.sort != null && isHiddenMarketAreaFieldCode(params.sort)
      ? undefined
      : params.sort;

  return {
    ...params,
    fields,
    sort,
  };
};
